// src/components/authentication/ProtectedRoute.js
/**
 * Dashboard access gate (DASH-AUTH-1).
 *
 * Usage
 * -----
 * ```jsx
 * <Route
 *   path="/patients"
 *   element={
 *     <ProtectedRoute>
 *       <PatientsPage />
 *     </ProtectedRoute>
 *   }
 * />
 * ```
 *
 * Behaviour
 * ---------
 * Only authenticated *and authorised* users may reach protected routes:
 *   1. No Firebase user                → redirect to `/auth` (sign-in).
 *   2. Membership check in progress    → loading spinner.
 *   3. Membership check failed         → deterministic "couldn’t verify" screen
 *                                         with retry (a transient backend error
 *                                         must not read as a permanent denial).
 *   4. Authenticated but not authorised → deterministic "access denied" screen.
 *   5. Authorised                       → render the protected children.
 *
 * Authorisation is decided by `AuthContext` from the backend User Service
 * (`GET /v1/users/me`); see `utils/authentication/dashboardAccess`.
 *
 * @param {{ children: React.ReactElement }} props
 * @returns {JSX.Element}
 */

import React from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import Spinner from "../Spinner";
import AccessDenied from "./AccessDenied";

export function ProtectedRoute({ children }) {
  const { user, authorised, contextLoading, contextError, refreshContext } =
    useAuth();

  // 1. Not signed in → send to the sign-in flow.
  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  // 2. Authoritative membership check still running.
  if (contextLoading && !authorised) {
    return <Spinner />;
  }

  // 3. Membership check could not complete — but the interim claims bridge may
  //    still authorise the user, so only block when we truly can't decide.
  if (contextError && !authorised) {
    return <AccessDenied reason="error" onRetry={refreshContext} />;
  }

  // 4. Signed in but not authorised for the dashboard.
  if (!authorised) {
    return <AccessDenied reason="denied" />;
  }

  // 5. Authorised.
  return children;
}
